import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const BatchUpdateModal = ({ isOpen, onClose, selectedLineIds, playId, onUpdateComplete }) => {
  const [activeTab, setActiveTab] = useState('glossary');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Glossary state
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [selectedGlossaryTerms, setSelectedGlossaryTerms] = useState([]);
  const [glossarySearchQuery, setGlossarySearchQuery] = useState('');

  // Audio status state
  const [audioStatus, setAudioStatus] = useState('no_change');

  // Metadata state
  const [metadataUpdates, setMetadataUpdates] = useState({
    pronunciation_help: '',
    context_notes: '',
    stage_direction: ''
  });
  const [updateMode, setUpdateMode] = useState('append'); // 'append' or 'replace'

  useEffect(() => {
    if (isOpen && playId) {
      fetchGlossaryTerms();
    }
  }, [isOpen, playId]);

  const fetchGlossaryTerms = async () => {
    try {
      const { data, error } = await supabase
        ?.from('glossary_terms')
        ?.select('*')
        ?.eq('play_id', playId)
        ?.order('term', { ascending: true });

      if (error) throw error;
      setGlossaryTerms(data || []);
    } catch (err) {
      console.error('Error fetching glossary terms:', err);
      setError('Failed to load glossary terms');
    }
  };

  const handleGlossaryTermToggle = (termId) => {
    setSelectedGlossaryTerms(prev =>
      prev?.includes(termId)
        ? prev?.filter(id => id !== termId)
        : [...prev, termId]
    );
  };

  const handleApplyGlossary = async () => {
    if (selectedGlossaryTerms?.length === 0) {
      setError('Please select at least one glossary term');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create assignments for each line and each selected term
      const assignments = [];
      for (const lineId of selectedLineIds) {
        for (const termId of selectedGlossaryTerms) {
          assignments?.push({
            line_id: lineId,
            glossary_term_id: termId
          });
        }
      }

      const { error: insertError } = await supabase
        ?.from('line_glossary_assignments')
        ?.upsert(assignments, { onConflict: 'line_id,glossary_term_id' });

      if (insertError) throw insertError;

      setSuccess(`Successfully assigned ${selectedGlossaryTerms?.length} glossary term(s) to ${selectedLineIds?.length} line(s)`);
      setTimeout(() => {
        onUpdateComplete?.();
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Error applying glossary assignments:', err);
      setError(err?.message || 'Failed to apply glossary assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyAudioStatus = async () => {
    if (audioStatus === 'no_change') {
      setError('Please select an audio status action');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (audioStatus === 'mark_complete') {
        // This is a placeholder - in real implementation, you'd need actual audio files
        setSuccess('Audio status marked as complete (placeholder)');
      } else if (audioStatus === 'mark_pending') {
        // Remove audio recordings for selected lines
        const { error: deleteError } = await supabase
          ?.from('audio_recordings')
          ?.delete()
          ?.in('line_id', selectedLineIds);

        if (deleteError) throw deleteError;

        setSuccess(`Successfully marked ${selectedLineIds?.length} line(s) as pending audio`);
      }

      setTimeout(() => {
        onUpdateComplete?.();
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Error updating audio status:', err);
      setError(err?.message || 'Failed to update audio status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyMetadata = async () => {
    const hasUpdates = Object.values(metadataUpdates)?.some(val => val?.trim() !== '');
    if (!hasUpdates) {
      setError('Please enter at least one metadata field to update');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Fetch current line data if appending
      let updates = {};

      if (updateMode === 'replace') {
        // Replace mode: directly set new values
        if (metadataUpdates?.pronunciation_help?.trim()) {
          updates.pronunciation_help = metadataUpdates?.pronunciation_help?.trim();
        }
        if (metadataUpdates?.context_notes?.trim()) {
          updates.context_notes = metadataUpdates?.context_notes?.trim();
        }
        if (metadataUpdates?.stage_direction?.trim()) {
          updates.stage_direction = metadataUpdates?.stage_direction?.trim();
        }

        const { error: updateError } = await supabase
          ?.from('lines')
          ?.update(updates)
          ?.in('id', selectedLineIds);

        if (updateError) throw updateError;
      } else {
        // Append mode: fetch existing data and append
        const { data: existingLines, error: fetchError } = await supabase
          ?.from('lines')
          ?.select('id, pronunciation_help, context_notes, stage_direction')
          ?.in('id', selectedLineIds);

        if (fetchError) throw fetchError;

        // Update each line individually for append mode
        for (const line of existingLines) {
          const lineUpdates = {};

          if (metadataUpdates?.pronunciation_help?.trim()) {
            lineUpdates.pronunciation_help = line?.pronunciation_help
              ? `${line?.pronunciation_help}\n${metadataUpdates?.pronunciation_help?.trim()}`
              : metadataUpdates?.pronunciation_help?.trim();
          }

          if (metadataUpdates?.context_notes?.trim()) {
            lineUpdates.context_notes = line?.context_notes
              ? `${line?.context_notes}\n${metadataUpdates?.context_notes?.trim()}`
              : metadataUpdates?.context_notes?.trim();
          }

          if (metadataUpdates?.stage_direction?.trim()) {
            lineUpdates.stage_direction = line?.stage_direction
              ? `${line?.stage_direction}\n${metadataUpdates?.stage_direction?.trim()}`
              : metadataUpdates?.stage_direction?.trim();
          }

          const { error: updateError } = await supabase
            ?.from('lines')
            ?.update(lineUpdates)
            ?.eq('id', line?.id);

          if (updateError) throw updateError;
        }
      }

      setSuccess(`Successfully updated metadata for ${selectedLineIds?.length} line(s)`);
      setTimeout(() => {
        onUpdateComplete?.();
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Error updating metadata:', err);
      setError(err?.message || 'Failed to update metadata');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveTab('glossary');
    setSelectedGlossaryTerms([]);
    setGlossarySearchQuery('');
    setAudioStatus('no_change');
    setMetadataUpdates({
      pronunciation_help: '',
      context_notes: '',
      stage_direction: ''
    });
    setUpdateMode('append');
    setError(null);
    setSuccess(null);
    onClose?.();
  };

  const filteredGlossaryTerms = glossaryTerms?.filter(term =>
    term?.term?.toLowerCase()?.includes(glossarySearchQuery?.toLowerCase()) ||
    term?.definition?.toLowerCase()?.includes(glossarySearchQuery?.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-card-foreground">Batch Update Lines</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedLineIds?.length} line(s) selected
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            iconName="X"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('glossary')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'glossary' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon name="BookOpen" size={16} />
              Glossary Assignments
            </div>
          </button>
          <button
            onClick={() => setActiveTab('audio')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'audio' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon name="Volume2" size={16} />
              Audio Status
            </div>
          </button>
          <button
            onClick={() => setActiveTab('metadata')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'metadata' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Icon name="FileText" size={16} />
              Metadata
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Glossary Tab */}
          {activeTab === 'glossary' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Search Glossary Terms
                </label>
                <Input
                  type="search"
                  placeholder="Search by term or definition..."
                  value={glossarySearchQuery}
                  onChange={(e) => setGlossarySearchQuery(e?.target?.value)}
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredGlossaryTerms?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      {glossarySearchQuery ? 'No glossary terms found' : 'No glossary terms available for this play'}
                    </p>
                  </div>
                ) : (
                  filteredGlossaryTerms?.map((term) => (
                    <div
                      key={term?.id}
                      onClick={() => handleGlossaryTermToggle(term?.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedGlossaryTerms?.includes(term?.id)
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedGlossaryTerms?.includes(term?.id)}
                          onChange={() => handleGlossaryTermToggle(term?.id)}
                          className="mt-1 w-4 h-4 rounded border-border"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-card-foreground">{term?.term}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{term?.definition}</p>
                          {term?.pronunciation && (
                            <p className="text-xs text-muted-foreground mt-1 italic">{term?.pronunciation}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Audio Status Tab */}
          {activeTab === 'audio' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-3">
                  Audio Status Action
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                    <input
                      type="radio"
                      name="audioStatus"
                      value="no_change"
                      checked={audioStatus === 'no_change'}
                      onChange={(e) => setAudioStatus(e?.target?.value)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium text-card-foreground">No Change</div>
                      <div className="text-sm text-muted-foreground">Keep existing audio status</div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                    <input
                      type="radio"
                      name="audioStatus"
                      value="mark_pending"
                      checked={audioStatus === 'mark_pending'}
                      onChange={(e) => setAudioStatus(e?.target?.value)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium text-card-foreground">Mark as Pending</div>
                      <div className="text-sm text-muted-foreground">Remove audio recordings and mark as pending</div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer">
                    <input
                      type="radio"
                      name="audioStatus"
                      value="mark_complete"
                      checked={audioStatus === 'mark_complete'}
                      onChange={(e) => setAudioStatus(e?.target?.value)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium text-card-foreground">Mark as Complete</div>
                      <div className="text-sm text-muted-foreground">Mark lines as having audio (placeholder)</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Update Mode
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUpdateMode('append')}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      updateMode === 'append' ?'border-primary bg-primary text-primary-foreground' :'border-border text-card-foreground hover:bg-muted'
                    }`}
                  >
                    Append
                  </button>
                  <button
                    onClick={() => setUpdateMode('replace')}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      updateMode === 'replace' ?'border-primary bg-primary text-primary-foreground' :'border-border text-card-foreground hover:bg-muted'
                    }`}
                  >
                    Replace
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {updateMode === 'append' ?'Add new content to existing metadata' :'Replace existing metadata with new content'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Pronunciation Help
                </label>
                <textarea
                  value={metadataUpdates?.pronunciation_help}
                  onChange={(e) => setMetadataUpdates(prev => ({ ...prev, pronunciation_help: e?.target?.value }))}
                  placeholder="Enter pronunciation guidance..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Context Notes
                </label>
                <textarea
                  value={metadataUpdates?.context_notes}
                  onChange={(e) => setMetadataUpdates(prev => ({ ...prev, context_notes: e?.target?.value }))}
                  placeholder="Enter context or background information..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Stage Direction
                </label>
                <textarea
                  value={metadataUpdates?.stage_direction}
                  onChange={(e) => setMetadataUpdates(prev => ({ ...prev, stage_direction: e?.target?.value }))}
                  placeholder="Enter stage directions..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={
              activeTab === 'glossary'
                ? handleApplyGlossary
                : activeTab === 'audio'
                ? handleApplyAudioStatus
                : handleApplyMetadata
            }
            disabled={isLoading}
            iconName={isLoading ? undefined : 'Check'}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Applying...
              </div>
            ) : (
              'Apply Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BatchUpdateModal;